/**
 * im-price-filter.js
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. Custom dual-range price slider (replaces broken native Shopify filter)
 * 2. Client-side price sort using metafield prices + active currency
 * 3. Collection redirect for Featured / Best Selling sort options
 * 4. Sort option whitelist (remove unwanted options from select)
 * ─────────────────────────────────────────────────────────────────────────────
 */
(() => {
  'use strict';

  /* ── Constants ── */

  const CURRENCIES     = ['EUR', 'GBP', 'USD'];
  const SYMBOLS        = { EUR: '€', GBP: '£', USD: '$' };

  // Sort options to keep (all others removed)
  const ALLOWED_SORT_VALUES = ['manual', 'price-ascending', 'price-descending'];

  // Price sorts are handled client-side (no page reload)
  const PRICE_SORT_VALUES = new Set(['price-ascending', 'price-descending']);

  /* ── Currency helpers ── */

  const getStoredCurrency = () => {
    try {
      const raw = localStorage.getItem('im_currency');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.currency && parsed.expiresAt > Date.now()) return parsed.currency.toUpperCase();
      if (CURRENCIES.includes(raw)) return raw;
    } catch (e) {}
    return null;
  };

  const getAutoCurrency = () => {
    const sw = document.querySelector('[data-global-currency-switcher]');
    return sw?.dataset?.autoCurrency?.toUpperCase() || 'EUR';
  };

  const getActiveCurrency = () => getStoredCurrency() || getAutoCurrency();

  /* ── Price parsing helpers ── */

  const parsePrice = (str) => {
    if (!str) return null;
    const cleaned = str.replace(/[^\d.,]/g, '').replace(/,/g, '.');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  const getCardPrice = (el, currency) => {
    const key = currency.toLowerCase() + 'Display';
    return parsePrice(el.dataset[key] || '');
  };

  const formatPrice = (value, currency) =>
    `${SYMBOLS[currency] || '€'}${value.toFixed(2)}`;

  /* ── Grid helpers ── */

  const getGrid = () =>
    document.querySelector('.collection-product-list, .collection ul.grid');

  const getGridItems = () => {
    const grid = getGrid();
    return grid ? Array.from(grid.querySelectorAll(':scope > li')) : [];
  };

  /* ── Price range scan ── */

  const scanPriceRange = (currency) => {
    const priceEls = document.querySelectorAll('.collection [data-im-price-switch]');
    let min = Infinity, max = -Infinity;
    priceEls.forEach(el => {
      const p = getCardPrice(el, currency);
      if (p !== null && p > 0) { if (p < min) min = p; if (p > max) max = p; }
    });
    if (!isFinite(min)) { min = 0; max = 100; }
    return { min: Math.floor(min), max: Math.ceil(max) };
  };

  /* ════════════════════════════════════════════════════════════════════════
     PRICE SLIDER
  ════════════════════════════════════════════════════════════════════════ */

  const buildSlider = (container) => {
    const currency = getActiveCurrency();
    const { min, max } = scanPriceRange(currency);
    container.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'im-price-slider';
    wrapper.dataset.currency = currency;

    // Header
    const header = document.createElement('div');
    header.className = 'im-price-slider__header';
    const rangeLabel = document.createElement('span');
    rangeLabel.className = 'im-price-slider__range-label';
    rangeLabel.textContent = `${formatPrice(min, currency)} — ${formatPrice(max, currency)}`;
    const resetBtn = document.createElement('button');
    resetBtn.type = 'button';
    resetBtn.className = 'im-price-slider__reset';
    resetBtn.textContent = 'Reset';
    resetBtn.style.display = 'none';
    header.appendChild(rangeLabel);
    header.appendChild(resetBtn);

    // Track
    const track = document.createElement('div');
    track.className = 'im-price-slider__track';
    const fill = document.createElement('div');
    fill.className = 'im-price-slider__fill';
    track.appendChild(fill);

    const inputMin = document.createElement('input');
    inputMin.type = 'range';
    inputMin.className = 'im-price-slider__input im-price-slider__input--min';
    inputMin.min = min; inputMin.max = max; inputMin.value = min; inputMin.step = 0.5;
    inputMin.setAttribute('aria-label', 'Minimum price');

    const inputMax = document.createElement('input');
    inputMax.type = 'range';
    inputMax.className = 'im-price-slider__input im-price-slider__input--max';
    inputMax.min = min; inputMax.max = max; inputMax.value = max; inputMax.step = 0.5;
    inputMax.setAttribute('aria-label', 'Maximum price');

    track.appendChild(inputMin);
    track.appendChild(inputMax);

    // Labels
    const labels = document.createElement('div');
    labels.className = 'im-price-slider__labels';
    const minLabel = document.createElement('span');
    minLabel.className = 'im-price-slider__label im-price-slider__label--min';
    minLabel.textContent = formatPrice(min, currency);
    const maxLabel = document.createElement('span');
    maxLabel.className = 'im-price-slider__label im-price-slider__label--max';
    maxLabel.textContent = formatPrice(max, currency);
    labels.appendChild(minLabel);
    labels.appendChild(maxLabel);

    wrapper.appendChild(header);
    wrapper.appendChild(track);
    wrapper.appendChild(labels);
    container.appendChild(wrapper);

    /* ── Slider logic ── */

    const updateFill = () => {
      const lo = parseFloat(inputMin.value), hi = parseFloat(inputMax.value);
      const range = max - min || 1;
      fill.style.left  = ((lo - min) / range * 100) + '%';
      fill.style.width = ((hi - lo) / range * 100) + '%';
    };

    const filterProducts = () => {
      const lo = parseFloat(inputMin.value), hi = parseFloat(inputMax.value);
      const cur = wrapper.dataset.currency;
      const isFullRange = (lo <= min && hi >= max);

      resetBtn.style.display = isFullRange ? 'none' : '';
      rangeLabel.textContent = isFullRange
        ? `${formatPrice(min, cur)} — ${formatPrice(max, cur)}`
        : `${formatPrice(lo, cur)} — ${formatPrice(hi, cur)}`;
      minLabel.textContent = formatPrice(lo, cur);
      maxLabel.textContent = formatPrice(hi, cur);

      const grid = getGrid();
      if (!grid) return;
      let visible = 0;
      grid.querySelectorAll(':scope > li').forEach(li => {
        const el = li.querySelector('[data-im-price-switch]');
        if (!el) { li.style.display = ''; visible++; return; }
        const price = getCardPrice(el, cur);
        const show = (price === null || (price >= lo && price <= hi));
        li.style.display = show ? '' : 'none';
        if (show) visible++;
      });

      // Count
      const countEl = document.querySelector('.product-count__text');
      if (countEl && !isFullRange) {
        countEl.textContent = `${visible} product${visible !== 1 ? 's' : ''}`;
      }

      // Empty state
      let emptyMsg = grid.parentElement.querySelector('.im-price-filter-empty');
      if (visible === 0) {
        if (!emptyMsg) {
          emptyMsg = document.createElement('p');
          emptyMsg.className = 'im-price-filter-empty';
          emptyMsg.textContent = 'No products in this price range.';
          grid.parentElement.insertBefore(emptyMsg, grid.nextSibling);
        }
        emptyMsg.style.display = '';
      } else if (emptyMsg) {
        emptyMsg.style.display = 'none';
      }
    };

    inputMin.addEventListener('input', () => {
      if (parseFloat(inputMin.value) > parseFloat(inputMax.value)) inputMin.value = inputMax.value;
      updateFill(); filterProducts();
    });

    inputMax.addEventListener('input', () => {
      if (parseFloat(inputMax.value) < parseFloat(inputMin.value)) inputMax.value = inputMin.value;
      updateFill(); filterProducts();
    });

    resetBtn.addEventListener('click', () => {
      inputMin.value = min; inputMax.value = max;
      updateFill(); filterProducts();
    });

    updateFill();
    wrapper._state = { inputMin, inputMax, rangeLabel, minLabel, maxLabel, fill, resetBtn, filterProducts, updateFill };
    return wrapper;
  };

  /* ── Replace native price filter ── */

  const init = () => {
    // Desktop disclosures
    document.querySelectorAll('.facets__disclosure').forEach(disclosure => {
      if (!disclosure.querySelector('price-range')) return;
      const display = disclosure.querySelector('.facets__display');
      if (!display || display.classList.contains('im-price-slider-container')) return;
      display.innerHTML = '';
      display.classList.add('im-price-slider-container');
      buildSlider(display);
    });

    // Vertical
    document.querySelectorAll('.facets__disclosure-vertical price-range').forEach(pr => {
      const parent = pr.closest('.facets__display-vertical');
      if (parent && !parent.classList.contains('im-price-slider-container')) {
        parent.innerHTML = '';
        parent.classList.add('im-price-slider-container');
        buildSlider(parent);
      }
    });

    // Mobile
    document.querySelectorAll('.mobile-facets__details').forEach(detail => {
      if (!detail.querySelector('price-range')) return;
      const submenu = detail.querySelector('.mobile-facets__submenu');
      if (!submenu || submenu.querySelector('.im-price-slider')) return;
      const closeBtn = submenu.querySelector('.mobile-facets__close-button');
      const footer = submenu.querySelector('.mobile-facets__footer');
      submenu.innerHTML = '';
      if (closeBtn) submenu.appendChild(closeBtn);
      const wrap = document.createElement('div');
      wrap.className = 'im-price-slider-container im-price-slider-container--mobile';
      buildSlider(wrap);
      submenu.appendChild(wrap);
      if (footer) submenu.appendChild(footer);
    });
  };

  /* ── Currency change: rebuild slider with new prices ── */

  document.addEventListener('im:currency:change', (e) => {
    const newCur = (e.detail?.currency || 'EUR').toUpperCase();
    document.querySelectorAll('.im-price-slider').forEach(wrapper => {
      const { min, max } = scanPriceRange(newCur);
      wrapper.dataset.currency = newCur;
      const s = wrapper._state;
      if (!s) return;
      s.inputMin.min = min; s.inputMin.max = max; s.inputMin.value = min;
      s.inputMax.min = min; s.inputMax.max = max; s.inputMax.value = max;
      s.rangeLabel.textContent = `${formatPrice(min, newCur)} — ${formatPrice(max, newCur)}`;
      s.minLabel.textContent = formatPrice(min, newCur);
      s.maxLabel.textContent = formatPrice(max, newCur);
      s.resetBtn.style.display = 'none';
      s.updateFill();
      // Show all products; re-apply any active price sort
      getGridItems().forEach(li => li.style.display = '');
      const emptyMsg = getGrid()?.parentElement?.querySelector('.im-price-filter-empty');
      if (emptyMsg) emptyMsg.style.display = 'none';
    });
    // Re-sort if a price sort is active
    const activeSort = document.querySelector('#SortBy')?.value || '';
    if (PRICE_SORT_VALUES.has(activeSort)) {
      sortProductsByPrice(activeSort === 'price-ascending' ? 'asc' : 'desc');
    }
  });

  /* ════════════════════════════════════════════════════════════════════════
     CLIENT-SIDE PRICE SORT
  ════════════════════════════════════════════════════════════════════════ */

  let _activePriceSort = null; // 'asc' | 'desc' | null

  const sortProductsByPrice = (direction) => {
    _activePriceSort = direction;
    const currency = getActiveCurrency();
    const grid = getGrid();
    if (!grid) return;

    const items = Array.from(grid.querySelectorAll(':scope > li'));
    items.sort((a, b) => {
      const elA = a.querySelector('[data-im-price-switch]');
      const elB = b.querySelector('[data-im-price-switch]');
      const pA = elA ? getCardPrice(elA, currency) : null;
      const pB = elB ? getCardPrice(elB, currency) : null;
      if (pA === null && pB === null) return 0;
      if (pA === null) return 1;
      if (pB === null) return -1;
      return direction === 'asc' ? pA - pB : pB - pA;
    });

    // Re-append in sorted order (preserves visibility/styles)
    items.forEach(li => grid.appendChild(li));

    // Update both sort selects to reflect the active price sort
    const sortValue = direction === 'asc' ? 'price-ascending' : 'price-descending';
    document.querySelectorAll('select[name="sort_by"]').forEach(sel => {
      // Temporarily remove change listener to avoid recursion
      sel.value = sortValue;
    });

    // Update sort label text (Dawn may update it via its own code)
    updateSortLabel(sortValue);
  };

  const updateSortLabel = (value) => {
    const LABELS = {
      'manual':           'Featured',
      'best-selling':     'Best selling',
      'price-ascending':  'Price, low to high',
      'price-descending': 'Price, high to low',
    };
    // Dawn renders the label inside .facet-filters__sort option selected text
    // The select value change above is enough for the UI
  };

  /* ════════════════════════════════════════════════════════════════════════
     SORT INTERCEPTION
  ════════════════════════════════════════════════════════════════════════ */

  const interceptSortChange = () => {
    // Use capture phase to fire BEFORE Dawn's facet-filters-form handler.
    // We intercept both 'input' AND 'change' because FacetFiltersForm listens
    // to 'input' events (which fire before 'change') and starts an 800ms debounce
    // that would call renderPage() and destroy the custom slider.
    const handler = (e) => {
      if (e.target.name !== 'sort_by') return;
      const value = e.target.value;

      // ── Client-side price sorts (instant, no page reload) ──
      if (PRICE_SORT_VALUES.has(value)) {
        e.stopImmediatePropagation();
        e.preventDefault();
        // Only execute the sort on 'change' (not on 'input') to avoid double-sorting
        if (e.type === 'change') {
          sortProductsByPrice(value === 'price-ascending' ? 'asc' : 'desc');
        }
        return;
      }

      // ── Featured (manual): let Shopify reload with sort_by=manual ──
      // No redirect — stays on current collection, Shopify handles ordering
    };
    document.addEventListener('change', handler, true); // capture phase
    document.addEventListener('input',  handler, true); // capture phase — stops debounce
  };

  /* ════════════════════════════════════════════════════════════════════════
     SORT OPTION WHITELIST — persistent via MutationObserver
     Shopify's facet-filters-form re-renders the select on every AJAX
     response, restoring all 8 options. We watch the DOM and strip
     disallowed options the instant they appear.
  ════════════════════════════════════════════════════════════════════════ */

  const stripSortOptions = (select) => {
    let changed = false;
    Array.from(select.options).forEach(opt => {
      if (!ALLOWED_SORT_VALUES.includes(opt.value)) { opt.remove(); changed = true; }
    });
    if (!ALLOWED_SORT_VALUES.includes(select.value)) {
      const fallback = select.querySelector('option[value="manual"]');
      if (fallback) { fallback.selected = true; changed = true; }
    }
    return changed;
  };

  const filterSortOptions = () => {
    document.querySelectorAll('select[name="sort_by"]').forEach(stripSortOptions);
  };

  // Watch for Shopify AJAX re-renders that restore the native price-range element
  // (e.g. after a non-price sort causes renderPage()). Re-run init() whenever a
  // fresh <price-range> appears so the custom slider is always shown.
  const watchPriceRange = () => {
    const observer = new MutationObserver(() => {
      const hasFreshPriceRange =
        document.querySelector('.facets__disclosure price-range') ||
        document.querySelector('.mobile-facets__details price-range');
      if (hasFreshPriceRange) init();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  // Watch for Shopify AJAX re-renders that restore unwanted options
  const watchSortSelects = () => {
    const observer = new MutationObserver((mutations) => {
      let needsStrip = false;
      for (const m of mutations) {
        // Check added nodes for sort selects
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.name === 'sort_by' || node.querySelector?.('[name="sort_by"]')) {
            needsStrip = true; break;
          }
        }
        // Check if options were added to an existing sort select
        if (m.target?.name === 'sort_by' || m.target?.closest?.('[name="sort_by"]')) {
          needsStrip = true;
        }
        if (needsStrip) break;
      }
      if (needsStrip) filterSortOptions();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  /* ════════════════════════════════════════════════════════════════════════
     BOOT
  ════════════════════════════════════════════════════════════════════════ */

  const boot = () => {
    filterSortOptions();
    watchSortSelects();
    watchPriceRange();
    interceptSortChange();

    // Check if a price sort is active in URL on page load
    const urlParams = new URLSearchParams(window.location.search);
    const urlSort = urlParams.get('sort_by');
    if (PRICE_SORT_VALUES.has(urlSort)) {
      // Page was loaded with price sort — do client-side sort after products render
      requestAnimationFrame(() => setTimeout(() => {
        sortProductsByPrice(urlSort === 'price-ascending' ? 'asc' : 'desc');
      }, 300));
    }

    const tryInit = () => {
      const priceRange = document.querySelector('.facets__disclosure price-range') ||
                         document.querySelector('.mobile-facets__details price-range');
      if (priceRange) { init(); return true; }
      return false;
    };

    if (tryInit()) return;

    const observer = new MutationObserver(() => {
      if (tryInit()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => { observer.disconnect(); tryInit(); }, 8000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    requestAnimationFrame(() => setTimeout(boot, 100));
  }
})();
